import { lineString, point, polygon } from "@turf/helpers";
import midpoint from "@turf/midpoint";
import type { Feature, LineString, Point, Polygon, Position } from "geojson";

import { getFeatureId } from "@/features/geojson_editor/libs/geojson/geometryUtils";
import { uuidv4 } from "@/features/utils/utils";

export enum META {
  midpoint = "midpoint",
  vertex = "vertex",
  line = "line",
  fill = "fill",
}

interface MetaFeatureConfig {
  includeFill?: boolean;
  includeLine?: boolean;
  includeVertex?: boolean;
  includeCenterPoint?: boolean;
}

type MetaFeatureProps = {
  // ID of the feature that this meta feature is associated with
  metaFeatureId: string;
  // Type of the meta feature
  metaType: META;
  // Index of the coordinate that this meta feature is associated with
  metaCoordinateIdx?: string;
};
export type MetaFeaturePoint = Feature<
  Point,
  MetaFeatureProps & { metaType: META.vertex; metaCoordinateIdx: string }
>;
export type MetaFeatureLine = Feature<
  LineString,
  MetaFeatureProps & { metaType: META.line }
>;
export type MetaFeatureFill = Feature<
  Polygon,
  MetaFeatureProps & { metaType: META.fill }
>;
export type MetaFeatureMidpoint = Feature<
  Point,
  MetaFeatureProps & { metaType: META.midpoint; metaCoordinateIdx: string }
>;
export type MetaFeature =
  | MetaFeaturePoint
  | MetaFeatureLine
  | MetaFeatureFill
  | MetaFeatureMidpoint;

/**
 * Remove the last coordinate if it is the same as the first coordinate.
 */
const removeLastCoordinateIfPresent = (ring: Position[]) => {
  return ring.length > 1 &&
    JSON.stringify(ring[0]) === JSON.stringify(ring[ring.length - 1])
    ? ring.slice(0, -1)
    : ring;
};

/**
 * Create editor meta data from a feature.
 */
export const createEditorMetaFromFeature = (
  feature: Feature,
  config: MetaFeatureConfig = {
    includeCenterPoint: true,
    includeFill: true,
    includeLine: true,
    includeVertex: true,
  },
): MetaFeature[] => {
  const features: MetaFeature[] = [];
  const featureId = getFeatureId(feature);

  // Helper function to add center points for line segments longer than a specified length.
  const addSideCenterPoints = (
    ring: Position[],
    ringIndex: number,
    polyIndex: number | null = null,
  ) => {
    for (let i = 0; i < ring.length - 1; i++) {
      const pt1 = point(ring[i]);
      const pt2 = point(ring[i + 1]);
      const midPt = midpoint(pt1, pt2);
      midPt.properties = {
        id: uuidv4(),
        metaCoordinateIdx:
          polyIndex !== null
            ? `${polyIndex}.${ringIndex}.${i}`
            : `${ringIndex}.${i}`,
        metaFeatureId: featureId,
        metaType: META.midpoint,
      };
      features.push(midPt as MetaFeatureMidpoint);
    }
  };

  switch (feature.geometry.type) {
    case "Polygon":
      if (config.includeFill) {
        features.push(
          polygon(feature.geometry.coordinates, {
            id: uuidv4(),
            metaFeatureId: featureId,
            metaType: META.fill,
          }),
        );
      }
      feature.geometry.coordinates.forEach((ring, ringIndex) => {
        if (config.includeVertex) {
          const cleanedRing = removeLastCoordinateIfPresent(ring);
          cleanedRing.forEach((coordinate, coordIndex) => {
            features.push(
              point(coordinate, {
                id: uuidv4(),
                metaCoordinateIdx: `${ringIndex}.${coordIndex}`,
                metaFeatureId: featureId,
                metaType: META.vertex,
              }),
            );
          });
        }
        if (config.includeLine && ring.length > 1) {
          const lineFeat = lineString(ring, {
            id: uuidv4(),
            metaFeatureId: featureId,
            metaType: META.line,
          });
          features.push(lineFeat as MetaFeatureLine);
        }
        if (config.includeCenterPoint) {
          addSideCenterPoints(ring, ringIndex);
        }
      });
      break;

    case "MultiPolygon":
      feature.geometry.coordinates.forEach((polygonCoords, polyIndex) => {
        if (config.includeFill) {
          features.push(
            polygon(polygonCoords, {
              id: uuidv4(),
              metaFeatureId: featureId,
              metaType: META.fill,
            }),
          );
        }
        polygonCoords.forEach((ring, ringIndex) => {
          if (config.includeVertex) {
            const cleanedRing = removeLastCoordinateIfPresent(ring);
            cleanedRing.forEach((coordinate, coordIndex) => {
              features.push(
                point(coordinate, {
                  id: uuidv4(),
                  metaCoordinateIdx: `${polyIndex}.${ringIndex}.${coordIndex}`,
                  metaFeatureId: featureId,
                  metaType: META.vertex,
                }),
              );
            });
          }
          if (config.includeLine && ring.length > 1) {
            const lineFeat = lineString(ring, {
              id: uuidv4(),
              metaFeatureId: featureId,
              metaType: META.line,
            });
            features.push(lineFeat as MetaFeatureLine);
          }
          if (config.includeCenterPoint) {
            addSideCenterPoints(ring, ringIndex, polyIndex);
          }
        });
      });
      break;

    case "LineString":
      if (config.includeLine) {
        const lineFeat = lineString(feature.geometry.coordinates, {
          id: uuidv4(),
          metaFeatureId: featureId,
          metaType: META.line,
        });
        features.push(lineFeat as MetaFeatureLine);
      }
      if (config.includeVertex) {
        feature.geometry.coordinates.forEach((coordinate, idx) => {
          features.push(
            point(coordinate, {
              id: uuidv4(),
              metaCoordinateIdx: `${idx}`,
              metaFeatureId: featureId,
              metaType: META.vertex,
            }),
          );
        });
      }
      if (config.includeCenterPoint) {
        addSideCenterPoints(feature.geometry.coordinates, 0);
      }
      break;

    case "MultiLineString":
      feature.geometry.coordinates.forEach((lineCoords, lineIndex) => {
        if (config.includeLine) {
          const multiLineFeat = lineString(lineCoords, {
            id: uuidv4(),
            metaFeatureId: featureId,
            metaType: META.line,
          });
          features.push(multiLineFeat as MetaFeatureLine);
        }
        if (config.includeVertex) {
          lineCoords.forEach((coordinate, coordIndex) => {
            features.push(
              point(coordinate, {
                id: uuidv4(),
                metaCoordinateIdx: `${lineIndex}.${coordIndex}`,
                metaFeatureId: featureId,
                metaType: META.vertex,
              }),
            );
          });
        }
        if (config.includeCenterPoint) {
          addSideCenterPoints(lineCoords, lineIndex);
        }
      });
      break;

    case "Point":
      if (config.includeVertex) {
        features.push(
          point(feature.geometry.coordinates, {
            id: uuidv4(),
            metaCoordinateIdx: "0",
            metaFeatureId: featureId,
            metaType: META.vertex,
          }),
        );
      }
      break;

    case "MultiPoint":
      if (config.includeVertex) {
        feature.geometry.coordinates.forEach((coordinate, idx) => {
          features.push(
            point(coordinate, {
              id: uuidv4(),
              metaCoordinateIdx: `${idx}`,
              metaFeatureId: featureId,
              metaType: META.vertex,
            }),
          );
        });
      }
      break;

    default:
      throw new Error(`Unsupported geometry type: ${feature.geometry.type}`);
  }
  return features;
};
